import { useBehavior } from '@libs/hooks';
import { TWidgetProps } from '@libs/types';
import { numberFormat, significantDecimalPlaces } from '@libs/utils';
import React, { Activity } from 'react';
import { TLimitBarData } from '../../types/data';
import { TLimitBarOption, TTextOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { TextCommon } from '../UI/Text/TextCommon';
import styles from './limitBar.module.css';
import ProgressBar from './ProgressBar';

const getMaximumFractionDigits = (amount: number) => {
    const v = significantDecimalPlaces(amount);
    return v > 2 ? 2 : v;
};

export const LimitBar: React.FC<TWidgetProps<TLimitBarData, TLimitBarOption>> = ({ widget }) => {
    const { currencyIso, currentAmount = 0, maxAmount = 0 } = widget.data?.value ?? {};
    const { limitTitle, currentSubtitle, maxSubtitle } = widget.data?.messages ?? {};
    const { margin, isError } = widget.options ?? {};
    const iso = currencyIso?.toLowerCase?.();
    const { isHidden } = useBehavior(widget);

    const purple = isError ? 'pink_100' : 'purple_100',
        grey = 'grey_72_lc';

    const purpleOptions: TTextOption = { variant: 'header', textColor: purple };
    const greyOptions: TTextOption = { variant: 'main', textColor: grey, textAlign: 'end' };

    const leftAmount = maxAmount - currentAmount;
    const progress = (currentAmount / maxAmount) * 100;

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div className={styles.limitBarContainer} style={getInlineStyles({ margin })} data-test={widget.uid}>
                <TextCommon isWidget={false} content={limitTitle} />
                <ProgressBar isError={!!isError} progress={progress} />
                <div className={styles.row} data-test={'amounts_' + widget.uid}>
                    <TextCommon isWidget={false} options={purpleOptions}>
                        {numberFormat(currentAmount, getMaximumFractionDigits(currentAmount)) as string}{' '}
                        <TextCommon isWidget={false} as={'span'} options={{ ...purpleOptions, smallCaps: true }} content={iso} />
                    </TextCommon>
                    <TextCommon isWidget={false} options={greyOptions}>
                        {numberFormat(leftAmount, getMaximumFractionDigits(leftAmount)) as string}{' '}
                        <TextCommon
                            isWidget={false}
                            as={'span'}
                            options={{
                                ...greyOptions,
                                smallCaps: true,
                            }}
                            content={iso}
                        />
                    </TextCommon>
                </div>
                <div className={styles.row} data-test={'text_' + widget.uid}>
                    <TextCommon isWidget={false} content={currentSubtitle} options={{ ...purpleOptions, variant: 'secondary' }} />
                    <TextCommon isWidget={false} content={maxSubtitle} options={{ ...greyOptions, variant: 'secondary' }} />
                </div>
            </div>
        </Activity>
    );
};
